/**
 * Client-side catalog integrity flags for the staged queue.
 * Keep heuristics aligned with `src/utils/catalogIntegrity.ts` / `lensIdentity.ts`.
 */

const CHROME_SPEC_KEY =
  /^(search\s*icon|filter\s*icon|cookie|sign[\s-]?in|log[\s-]?in|menu|nav|cart|wishlist|available variants?|selected variant|other specs?|japanese technology|lumix pro services|original price|current price)$/i;

const JUNK_KEY =
  /^(available variants?|selected variant|other specs?|japanese technology|enhanced control and reliability|close up creativity|lumix pro services|original price|current price|discounted price|style|quantity|contact(?:\s+pro)?|explore)$/i;

const MARKETING_VALUE =
  /world-renowned|inspired by|designed to|discover the|experience the|whether you.?re|perfect for creators|enjoy wireless|stream live|free your creativity|go live/i;

const MARKETING_SPEC_VALUE =
  /battery life is doubled|for extended shooting|not included with the dmw-|by using two batteries/i;

const RETAILER_STOREFRONT_MENTION =
  /\bb\s*&\s*h\b|\bbh\s*photo|\bbhphotovideo|\bb\s+and\s+h\s+photo|\bbandhphoto|\badorama\b|\bbedford\s+camera\b/i;

const FEATURE_HEADLINE_KEY =
  /^(free your creativity|wireless live streaming|cinema, broadcasting|advanced functions to support|fully weather sealed)\b/i;

const TECHNICAL_KEY =
  /construction|mount|stabilizer|focal|aperture|focus|magnification|angle of view|dust|splash|temperature|filter|diameter|length|weight|accessor|elements|diaphragm|closest|minimum|maximum|sensor|iso|shutter|resolution|viewfinder|lcd|battery|wifi|bluetooth|video|codec|recording|card|slot|dimension|pixels|continuous|buffer|media|format|sync|coverage/i;

const LENS_KIT_NAME =
  /\b(lens\s+kit|camera\s+kit)\b|\bbody\s*\+|\bwith\s+.{0,60}\blens(?:es)?\b|\+\s*.{0,50}\blens(?:es)?\b/i;

const LENS_ACCESSORY_NAME =
  /\b(caps?|hoods?|filters?|adapters?|rings?|protectors?|pouches?|cases?|covers?)\b/i;

function looksLikeCameraBodyLensKitName(name: string): boolean {
  const n = String(name || '');
  if (!LENS_KIT_NAME.test(n)) return false;
  if (
    /\b(cage|hood|grip|handle|cloth|wrap|filter holder|step[\s-]?up|step[\s-]?down)\b/i.test(n) &&
    !/\b(mirrorless|dslr|compact|point\s*[&+]?\s*shoot|digital camera|camcorder)\b/i.test(n)
  ) {
    return false;
  }
  return (
    /\b(mirrorless|dslr|compact|point\s*[&+]?\s*shoot|digital camera|camcorder|body)\b/i.test(n) ||
    /\bmark\s+[ivx0-9]+\b/i.test(n)
  );
}

function looksLikeCameraWithBuiltInOrKitLensName(name: string): boolean {
  const n = String(name || '');
  if (looksLikeCameraBodyLensKitName(n)) return true;
  return (
    /\b(point\s*[&+]?\s*shoot|digital camera|compact camera|bridge camera|mirrorless camera)\b/i.test(n) &&
    /\b(zoom\s+lens|\d+\s*-\s*\d+\s*mm|\+\s*.{0,40}lens)\b/i.test(n)
  );
}

const POWER_ACCESSORY_NAME =
  /\b(batter(?:y|ies)|chargers?|dc\s*couplers?|dummy\s*batter(?:y|ies)|ac\s*adapters?)\b/i;

const CARRY_ACCESSORY_NAME = /\b(bags?|backpacks?|pouch(?:es)?|holsters?|slings?|cases?)\b/i;

const CAMERA_SKU_TITLE =
  /\b(?:mirrorless|dslr|slr|compact|bridge|cinema|digital)\s+cameras?\b|\bcameras?\s+(?:body|kit)\b|\bcamera\s+body\b|\bcamcorders?\b|\bpoint\s*[&+]?\s*shoot\b/i;

function looksLikeCameraSoldWithAccessory(name: string): boolean {
  return CAMERA_SKU_TITLE.test(name) && /\bwith\b/i.test(name);
}

function looksLikeDedicatedBatteryOrChargerName(name: string): boolean {
  const n = String(name || '');
  if (!POWER_ACCESSORY_NAME.test(n)) return false;
  if (looksLikeCameraWithBuiltInOrKitLensName(n)) return false;
  if (/\b(batter(?:y|ies)|chargers?)\s+(?:grip\s+)?for\b/i.test(n)) return true;
  if (/\bbattery\s+grips?\b/i.test(n) && !CAMERA_SKU_TITLE.test(n)) return true;
  if (looksLikeCameraSoldWithAccessory(n)) return false;
  if (CAMERA_SKU_TITLE.test(n) || /\bcamera\s+kit\b/i.test(n)) return false;
  return true;
}

function looksLikeDedicatedCarryAccessoryName(name: string): boolean {
  const n = String(name || '');
  const primary = n.replace(/\([^)]*\)/g, ' ');
  if (!CARRY_ACCESSORY_NAME.test(primary)) return false;
  if (looksLikeCameraWithBuiltInOrKitLensName(n) || looksLikeCameraBodyLensKitName(n)) return false;
  if (looksLikeCameraSoldWithAccessory(n)) return false;
  if (/\bcamera\s+kit\b/i.test(n)) return false;
  if (/\b(?:cinema\s+)?lens(?:es)?\s+kit\b|\bkit\s+with\s+\d+\s+lenses?\b/i.test(primary)) return false;
  if ((primary.match(/\d+\s*mm/gi) || []).length >= 2) return false;
  // "50mm with Case" / "EOS R5 with Leather Case" — carry gear only in a with-clause
  // is bundled, not the SKU. "Travel Bag with Pouch" still has a carry noun left.
  if (/\bwith\b.{0,60}\b(bags?|backpacks?|pouch(?:es)?|holsters?|slings?|cases?)\b/i.test(primary)) {
    const withoutBundled = primary.replace(
      /\bwith\b.{0,60}\b(bags?|backpacks?|pouch(?:es)?|holsters?|slings?|cases?)\b/gi,
      ' ',
    );
    if (!CARRY_ACCESSORY_NAME.test(withoutBundled)) return false;
  }
  return true;
}

function looksLikeDedicatedAccessoryMisTypedAsBody(name: string): boolean {
  return looksLikeDedicatedBatteryOrChargerName(name) || looksLikeDedicatedCarryAccessoryName(name);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

const HAS_UNITS = /(?:\d|mm\b|f\/?\d|°|\bg\b|\blb\b|\boz\b|fps|\bev\b|\biso\b)/i;

function looksLikeFeatureHeadline(key: string): boolean {
  const k = String(key || '').replace(/\s+/g, ' ').trim();
  if (!k) return false;
  if (FEATURE_HEADLINE_KEY.test(k)) return true;
  // Two-sentence slogans. Avoid "I.S." / "O.I.S." and technical "Approx. Weight".
  if (/(?:[a-z]{3,}|!)\.\s+[A-Z]/.test(k) && !TECHNICAL_KEY.test(k)) return true;
  if (/[.!]$/.test(k) && k.split(/\s+/).length >= 4) return true;
  const words = k.split(/\s+/).filter(Boolean);
  return words.length >= 6 && !TECHNICAL_KEY.test(k);
}

function looksLikeMarketingParagraph(value: string): boolean {
  const v = String(value || '').replace(/\s+/g, ' ').trim();
  if (v.length < 80) return false;
  if (/^(enjoy|discover|combines|featuring|hybrid mirrorless)\b/i.test(v)) return true;
  const sentences = v.split(/(?<=[.!?])\s+(?=[A-Z])/).filter((s) => s.length > 25);
  return v.length > 140 && sentences.length >= 2;
}

function looksLikeTruncatedBatteryPack(value: string): boolean {
  const v = String(value || '').replace(/\s+/g, ' ').trim();
  if (!/^\d+(\.\d+)?\s*V\b/i.test(v)) return false;
  if (!/\(sold separately\)/i.test(v)) return false;
  const withoutSold = v.replace(/\s*\(sold separately\)\s*$/i, '');
  return /\)/.test(withoutSold) && !/\(/.test(withoutSold);
}

/** Inch marks in spec labels (`4/3"`, `1/4"`) are not storefront chrome. */
function keyHasNonInchQuote(key: string): boolean {
  return /"/.test(key.replace(/\d+(?:\s*\/\s*\d+)?(?:\.\d+)?\s*"/g, ''));
}

function isStorefrontJunkSpec(key: string, value: string): boolean {
  const k = String(key || '').replace(/\s+/g, ' ').trim();
  const v = String(value || '').replace(/\s+/g, ' ').trim();
  if (!k || !v) return true;
  if (JUNK_KEY.test(k) || CHROME_SPEC_KEY.test(k)) return true;
  if (k.includes('|') && (k.includes('[') || k.includes(']'))) return true;
  // Inch marks (`4/3"`, `1/4"`) are real spec labels, not theme/JSON chrome.
  if (keyHasNonInchQuote(k) || /close subtotal|navbar-height|sticky-bar|cart-link|instafeed|text-align/i.test(k)) return true;
  if (
    /\b(z-index|box-shadow|text-overflow|white-space|justify-content|flex-flow|min-height|max-width|btn-transform|line-height|text-align|padding-bottom|padding-top|margin-top|margin-bottom)\b/i.test(k)
    || /^(weight|\([^)]+\))\s+(position|bottom|left|right|top|width|height|background|flex|padding|display|overflow|color)$/i.test(k)
    || (
      /\b(position|background|padding|margin|overflow|flex)\b/i.test(k)
      && /var\(--|!important|rgb\(|#insta|inline-flex|absolute;?$|^\d+(px|%)?;?$/i.test(v)
    )
  ) {
    return true;
  }
  if (/store locator|shop by collection|shop all shop|long hill cross|taxes and shipping calculated|mobile-sticky-bar|iphone\|cpu|instafeed|typeof\s+\w+|px;\s*\}|var\(--|!important|inline-flex|#insta|@media\s*\(/i.test(v)) return true;
  if (MARKETING_SPEC_VALUE.test(v)) return true;
  if (RETAILER_STOREFRONT_MENTION.test(v.replace(/&amp;/gi, '&'))) return true;
  if (looksLikeTruncatedBatteryPack(v)) return true;
  if (looksLikeFeatureHeadline(k)) return true;
  if (looksLikeMarketingParagraph(v) && (k.split(/\s+/).length >= 5 || !TECHNICAL_KEY.test(k))) {
    return true;
  }
  const technical = TECHNICAL_KEY.test(`${k} ${v}`);
  if (MARKETING_VALUE.test(v) && !technical && v.length > 120 && !HAS_UNITS.test(v)) return true;
  if (k.length > 80 && !technical) return true;
  return false;
}

/** True for a dedicated lens PDP, not a camera+lens kit or lens accessory. */
export function looksLikeStandaloneLensName(name: string): boolean {
  const n = String(name || '');
  if (!n.trim()) return false;
  if (LENS_KIT_NAME.test(n) || LENS_ACCESSORY_NAME.test(n)) return false;
  if (/camera[- ]lens(?:es)?\b/i.test(n)) return true;
  return /\blens(?:es)?\b/i.test(n) && /\d+\s*mm/i.test(n);
}

export function specificationTreeHasChrome(node: unknown): boolean {
  let found = false;
  const walk = (value: unknown, key = '') => {
    if (found || value == null) return;
    const label = String(key || '').trim();
    if (label && typeof value === 'string' && isStorefrontJunkSpec(label, value)) {
      found = true;
      return;
    }
    if (label && CHROME_SPEC_KEY.test(label)) {
      found = true;
      return;
    }
    if (isPlainObject(value)) {
      for (const [childKey, child] of Object.entries(value)) {
        if (childKey === 'raw' || childKey === '_meta') continue;
        walk(child, childKey);
      }
    }
  };
  walk(node);
  return found;
}

export function looksLikeSuspiciousCentsPrice(
  pricing: { msrp?: number; retail?: number; salePrice?: number } | undefined,
  name: string,
): boolean {
  if (!looksLikeStandaloneLensName(name) || !pricing) return false;
  return ['msrp', 'retail', 'salePrice'].some((key) => {
    const value = pricing[key as keyof typeof pricing];
    return typeof value === 'number' && Number.isFinite(value) && value > 0 && value < 25;
  });
}

export function stagedIntegrityIssueIds(product: {
  name?: string;
  productType?: string;
  pricing?: { msrp?: number; retail?: number; salePrice?: number };
  specifications?: unknown;
  specificationsRaw?: unknown;
}): string[] {
  const ids: string[] = [];
  if (
    specificationTreeHasChrome(product.specifications) ||
    specificationTreeHasChrome(product.specificationsRaw)
  ) {
    ids.push('junk_specs');
  }
  if (looksLikeStandaloneLensName(String(product.name || '')) && product.productType === 'camera') {
    ids.push('lens_as_camera');
  }
  if (looksLikeCameraWithBuiltInOrKitLensName(String(product.name || '')) && product.productType === 'lens') {
    ids.push('kit_as_lens');
  }
  if (
    (product.productType === 'camera' || product.productType === 'lens') &&
    looksLikeDedicatedAccessoryMisTypedAsBody(String(product.name || ''))
  ) {
    ids.push('accessory_as_camera');
  }
  if (looksLikeSuspiciousCentsPrice(product.pricing, String(product.name || ''))) {
    ids.push('suspicious_price');
  }
  return ids;
}
